import { useRef } from "react";
import { useTranslation } from "react-i18next";

import type { TourDefinition } from "../types";
import { useTour } from "../useTour";
import { useTourDuration } from "../useTourDuration";
import { getWelcomeFeatures } from "../welcomeFeatures";
import { tourSurfaceVariants } from "./tourSurfaceVariants";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import { CaptainAvatar } from "@/features/captain/components/CaptainAvatar";
import { cn } from "@/lib/utils/cn";

type TourWelcomeProps = {
  definition: TourDefinition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStart: () => void;
};

export function TourWelcome({ definition, open, onOpenChange, onStart }: TourWelcomeProps) {
  const { t } = useTranslation();
  const { isRunning, stepIds } = useTour();
  const duration = useTourDuration();
  const startRef = useRef<HTMLButtonElement>(null);
  const key = (part: string) => `${definition.copyKey}.welcome.${part}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-0 overflow-hidden p-0 sm:max-w-[35rem]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          startRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => isRunning && event.preventDefault()}
      >
        <div className={cn(tourSurfaceVariants(), "relative flex flex-col gap-3 px-7 pb-6 pt-7")}>
          <Badge variant="info" className="w-fit">
            {t(key("badge"))}
          </Badge>
          <DialogTitle className="pr-24 text-xl font-semibold leading-6">{t(key("title"))}</DialogTitle>
          <DialogDescription className="pr-24 text-sm leading-[1.3] text-foreground">
            {t(key("body"), { duration })}
          </DialogDescription>
          <CaptainAvatar className="absolute right-7 top-7 size-18" />
        </div>
        <ul className="flex flex-col gap-4 px-7 py-5">
          {getWelcomeFeatures(definition, stepIds).map(({ key: feature, Icon, descriptionPart }) => (
            <li key={feature} className="flex items-start gap-3.5">
              <span className="selected-tint-primary flex size-9 shrink-0 items-center justify-center rounded-full text-primary">
                <Icon aria-hidden className="size-5" />
              </span>
              <div className="flex flex-col gap-0.5">
                <p className="text-sm font-semibold">{t(key(`${feature}.title`))}</p>
                <p className="text-xs text-muted-foreground">{t(key(`${feature}.${descriptionPart}`))}</p>
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter className="items-center gap-4 border-t px-7 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t(key("notNow"))}
          </Button>
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted-foreground">{t(key("meta"), { count: stepIds.length, duration })}</span>
            <Button ref={startRef} onClick={onStart}>{t(key("start"))}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
